import React, { useEffect, useState, useMemo } from 'react';
import { Controller, useWatch, useFormContext } from 'react-hook-form';
import { X } from 'lucide-react';
import { DropdownFloating } from '@components/ui/dropdown-floating';
import { FloatingInput } from '@components/ui/input-floating';
import { useHealthStore } from 'src/store/health';
import { REQUIRE_DETAILS } from '@features/onboarding/components/6-health-question-form/constants';
import { FormMessageSpan } from '@components/ui/form';
import { DosageOption } from 'src/services/interfaces/health';
import { Medication } from 'src/services/interfaces/application';

const FREQUENCY_OPTIONS = [
  { value: 'Once daily', label: 'Once daily' },
  { value: 'Twice daily', label: 'Twice daily' },
  { value: 'Three times daily', label: 'Three times daily' },
  { value: 'Four times daily', label: 'Four times daily' },
  { value: 'Every X hours', label: 'Every X hours' },
  { value: 'Every other day', label: 'Every other day' },
  { value: 'Three times per week', label: 'Three times per week' },
  { value: 'Once weekly', label: 'Once weekly' },
  { value: 'Every 2 weeks', label: 'Every 2 weeks' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'One-time dose', label: 'One-time dose' },
  { value: 'As needed', label: 'As needed' },
];

const OTHER_DOSAGE_VALUE = '__OTHER__';

interface BrandGenericInfo {
  brandName: string;
  brandId: string;
  genericName: string;
  genericId: string;
  originalType: 'brand' | 'generic';
}

interface MedicationListProps {
  fields: any[];
  control: any;
  questionId: string;
  dynamicRecipientOptions: { value: string; label: string }[];
  remove: (index: number) => void;
  append: (value: any) => void;
  onApplicantChange?: (applicant: string, index: number) => void;
  brandGenericMap?: Record<string, BrandGenericInfo>;
}

export const MedicationList: React.FC<MedicationListProps> = ({
  fields,
  control,
  questionId,
  dynamicRecipientOptions,
  remove,
  append,
  onApplicantChange,
  brandGenericMap = {},
}) => {
  const { isFailValidation, getMedicationDosages, setFailValidation } =
    useHealthStore();
  const { setValue } = useFormContext();

  // Store dosages for each medication by medication id
  const [medicationDosages, setMedicationDosages] = useState<
    Record<string, DosageOption[]>
  >({});
  const [loadingDosages, setLoadingDosages] = useState<Record<string, boolean>>(
    {}
  );

  // Single source of truth — watch the entire medications array
  const watchedMedications = useWatch({
    control,
    name: `healthQuestions.${questionId}.medications` as any,
    defaultValue: [],
  }) as Medication[];

  // Load dosages for medications when they are added
  useEffect(() => {
    watchedMedications.forEach((med: any) => {
      // Use the actual medication id from form data, not the field array id
      const medicationId = med?.id;
      if (
        medicationId &&
        !medicationDosages[medicationId] &&
        !loadingDosages[medicationId]
      ) {
        setLoadingDosages((prev) => ({ ...prev, [medicationId]: true }));
        getMedicationDosages(medicationId)
          .then((dosages) => {
            setMedicationDosages((prev) => ({
              ...prev,
              [medicationId]: dosages,
            }));
            setLoadingDosages((prev) => ({ ...prev, [medicationId]: false }));
          })
          .catch(() => {
            setLoadingDosages((prev) => ({ ...prev, [medicationId]: false }));
          });
      }
    });
  }, [
    watchedMedications,
    getMedicationDosages,
    medicationDosages,
    loadingDosages,
  ]);

  // Ensure medicationType is correctly set for brand medications on mount
  // RHF can lose custom field values across re-renders, so we re-apply from map
  useEffect(() => {
    watchedMedications.forEach((med: any, index: number) => {
      const info = (med as any)?._bgKey
        ? brandGenericMap[(med as any)._bgKey]
        : undefined;
      if (!info) return;
      const currentType = (med as any)?.medicationType;
      if (!currentType || (currentType !== 'brand' && currentType !== 'generic')) {
        const fieldName = `healthQuestions.${questionId}.medications.${index}`;
        setValue(`${fieldName}.medicationType`, info.originalType, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    });
  }, [watchedMedications, questionId, setValue, brandGenericMap]);

  return (
    <>
      {fields.map((field, index) => {
        // Use actual medication data from watchedMedications, not field array metadata
        const med = watchedMedications[index] || {};
        if (!med?.id) return null;
        const fieldName = `healthQuestions.${questionId}.medications.${index}`;

        // Get dosage options for this medication using the actual medication id
        const medicationId = med.id;
        const dosages = medicationDosages[medicationId] || [];
        const dosageOptions = [
          ...dosages.map((d) => ({ value: d.id, label: d.value })),
          { value: OTHER_DOSAGE_VALUE, label: 'Other dosage' },
        ];

        // Check if "Other dosage" is selected (__OTHER__ means "Other dosage" was selected)
        // undefined means no selection yet (should show placeholder)
        const currentDosageId = (med as any)?.dosageId;
        const currentDosageDescription = (med as any)?.dosageDescription;
        const hasDosageDescription =
          currentDosageDescription && currentDosageDescription.trim() !== '';
        // __OTHER__ means "Other dosage" was explicitly selected
        const isOtherDosageSelected = currentDosageId === OTHER_DOSAGE_VALUE;
        const hasDosageId =
          currentDosageId &&
          currentDosageId !== undefined &&
          currentDosageId !== OTHER_DOSAGE_VALUE;

        // Look up brand/generic info using the stable key set at addition time
        // Only brand medications that have a generic equivalent get a _bgKey
        const bgInfo = (med as any)._bgKey
          ? brandGenericMap[(med as any)._bgKey]
          : undefined;

        return (
          <div
            className="mt-4 p-4 rounded-lg bg-white card-shadow"
            key={field.id}
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold">
                  {med?.name || `Medication ID: ${med.id}`}
                </p>
              </div>
              <button
                className="cursor-pointer"
                onClick={() => {
                  remove(index);
                }}
              >
                <X size={20} className="text-primary" />
              </button>
            </div>
            {/* Brand vs Generic question — shown for medications with a generic equivalent */}
            {bgInfo && (
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <p className="text-sm">
                  Which version of this medication do you take?
                </p>
                <Controller
                  control={control}
                  name={`${fieldName}.medicationType`}
                  render={({ field: typeField }) => {
                    const currentType = typeField.value;
                    return (
                      <div className="flex gap-4">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name={`medicationType-${index}`}
                            value="generic"
                            checked={currentType === 'generic'}
                            onChange={() => {
                              typeField.onChange('generic');
                              setValue(`${fieldName}.id`, bgInfo.genericId, {
                                shouldDirty: true,
                              });
                              setValue(`${fieldName}.name`, bgInfo.genericName, {
                                shouldDirty: true,
                              });
                              setValue(`${fieldName}.dosageId`, undefined, {
                                shouldDirty: false,
                              });
                              setValue(`${fieldName}.dosageValue`, '', {
                                shouldDirty: false,
                              });
                              setValue(`${fieldName}.dosageDescription`, '', {
                                shouldDirty: false,
                              });
                              setValue(`${fieldName}.isNew`, true, {
                                shouldDirty: false,
                              });
                            }}
                            className="accent-primary w-4 h-4"
                          />
                          <span className="text-sm">Generic</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name={`medicationType-${index}`}
                            value="brand"
                            checked={currentType === 'brand'}
                            onChange={() => {
                              typeField.onChange('brand');
                              setValue(`${fieldName}.id`, bgInfo.brandId, {
                                shouldDirty: true,
                              });
                              setValue(`${fieldName}.name`, bgInfo.brandName, {
                                shouldDirty: true,
                              });
                              setValue(`${fieldName}.dosageId`, undefined, {
                                shouldDirty: false,
                              });
                              setValue(`${fieldName}.dosageValue`, '', {
                                shouldDirty: false,
                              });
                              setValue(`${fieldName}.dosageDescription`, '', {
                                shouldDirty: false,
                              });
                              setValue(`${fieldName}.isNew`, true, {
                                shouldDirty: false,
                              });
                            }}
                            className="accent-primary w-4 h-4"
                          />
                          <span className="text-sm">Branded</span>
                        </label>
                      </div>
                    );
                  }}
                />
              </div>
            )}
            <div className="mt-4 grid grid-cols-1 max-lg:gap-2 sm:grid-cols-3 gap-4">
              <div>
                <Controller
                  control={control}
                  name={`${fieldName}.dosageId`}
                  render={({ field }) => (
                    <>
                      <DropdownFloating
                        id={`dosage-${index}`}
                        options={dosageOptions}
                        value={
                          // If isNew is true and onChange hasn't been called yet, show empty value
                          (med as any)?.isNew === true
                            ? ''
                            : field.value === OTHER_DOSAGE_VALUE
                              ? OTHER_DOSAGE_VALUE
                              : field.value === undefined ||
                                  field.value === null ||
                                  field.value === ''
                                ? ''
                                : String(field.value)
                        }
                        label="Dosage"
                        placeholder={
                          loadingDosages[medicationId]
                            ? 'Loading dosages...'
                            : 'Select dosage'
                        }
                        emptyLabel="Select dosage"
                        inputClass="mb-auto min-h-[52px]"
                        error={
                          isFailValidation &&
                          field.value === undefined &&
                          !isOtherDosageSelected
                        }
                        onChange={(value) => {
                          // Clear validation errors when user starts editing
                          if (isFailValidation) {
                            setFailValidation(false);
                          }

                          // Set isNew to false when user makes a selection
                          if ((med as any)?.isNew === true) {
                            setValue(`${fieldName}.isNew`, false, {
                              shouldDirty: false,
                              shouldValidate: false,
                            });
                          }

                          // Store __OTHER__ when "Other dosage" is selected (will be converted to null in transformApplicationData)
                          if (value === OTHER_DOSAGE_VALUE) {
                            field.onChange(OTHER_DOSAGE_VALUE);
                            setValue(`${fieldName}.dosageValue`, '', {
                              shouldDirty: false,
                              shouldValidate: false,
                            });
                          } else if (value === '') {
                            // Empty selection - set to undefined to show placeholder
                            field.onChange(undefined);
                            setValue(`${fieldName}.dosageValue`, '', {
                              shouldDirty: false,
                              shouldValidate: false,
                            });
                          } else {
                            field.onChange(value);
                            // Store dosageValue (e.g. "10mg") for PDF - server needs human-readable dosage
                            const selectedOption = dosageOptions.find(
                              (opt) => opt.value === value
                            );
                            setValue(
                              `${fieldName}.dosageValue`,
                              selectedOption?.label ?? '',
                              { shouldDirty: false, shouldValidate: false }
                            );
                            setValue(`${fieldName}.dosageDescription`, '', {
                              shouldDirty: false,
                              shouldValidate: false,
                            });
                          }
                        }}
                        disabled={
                          loadingDosages[medicationId] || dosages.length === 0
                        }
                      />
                      {isFailValidation &&
                        !hasDosageId &&
                        !isOtherDosageSelected && (
                          <FormMessageSpan
                            className="mt-1 text-sm"
                            text={'Dosage cannot be blank.'}
                          />
                        )}
                    </>
                  )}
                />
              </div>
              <div>
                <Controller
                  control={control}
                  name={`${fieldName}.frequency`}
                  render={({ field, fieldState }) => (
                    <>
                      <DropdownFloating
                        id={`frequency-${index}`}
                        options={FREQUENCY_OPTIONS}
                        value={field.value || ''}
                        label="Frequency"
                        placeholder="Select frequency"
                        emptyLabel="Select frequency"
                        inputClass="mb-auto min-h-[52px]"
                        error={isFailValidation && !field.value}
                        onChange={(value) => {
                          if (isFailValidation) {
                            setFailValidation(false);
                          }
                          field.onChange(value);
                        }}
                      />
                      {isFailValidation && !field.value && (
                        <FormMessageSpan
                          className="mt-1 text-sm"
                          text={'Frequency cannot be blank.'}
                        />
                      )}
                    </>
                  )}
                />
              </div>
              <div>
                <Controller
                  control={control}
                  name={`${fieldName}.applicant`}
                  render={({ field, fieldState }) => (
                    <>
                      <DropdownFloating
                        id={`applicant-${index}`}
                        options={dynamicRecipientOptions}
                        value={
                          field.value != null && field.value !== ''
                            ? String(field.value)
                            : ''
                        }
                        label="Who takes this?"
                        inputClass="mb-auto min-h-[52px]"
                        onChange={(value) => {
                          if (isFailValidation) {
                            setFailValidation(false);
                          }
                          field.onChange(value);
                        }}
                        disabled={dynamicRecipientOptions.length <= 1}
                      />
                      {isFailValidation && !field.value && (
                        <FormMessageSpan
                          className="mt-1 text-sm"
                          text={'Recipient cannot be blank.'}
                        />
                      )}
                    </>
                  )}
                />
              </div>
            </div>
            {/* Show custom dosage description input if "Other dosage" is selected */}
            {isOtherDosageSelected && (
              <div className="mt-4">
                <Controller
                  control={control}
                  name={`${fieldName}.dosageDescription`}
                  render={({ field }) => {
                    const shouldShowError =
                      isFailValidation &&
                      (!field.value ||
                        (field.value && field.value.trim().length === 0));

                    return (
                      <>
                        <FloatingInput
                          id={`custom-dosage-${index}`}
                          label="Describe dosage (e.g., 10mg)"
                          type="text"
                          placeholder="Enter custom dosage"
                          inputClass="mb-auto min-h-[52px]"
                          value={field.value || ''}
                          maxLength={128}
                          onChange={(e) => {
                            if (isFailValidation) {
                              setFailValidation(false);
                            }
                            field.onChange(e);
                          }}
                          onBlur={(e) => {
                            const trimmed = (e.target.value || '').trim();
                            if (trimmed !== e.target.value) {
                              field.onChange(trimmed);
                            }
                            field.onBlur();
                          }}
                        />
                        {shouldShowError && (
                          <FormMessageSpan
                            className="mt-1 text-sm"
                            text={'Custom dosage description is required.'}
                          />
                        )}
                      </>
                    );
                  }}
                />
              </div>
            )}
            {/* Show custom frequency input if "Every X hours" or "As needed" is selected */}
            {((med as any)?.frequency === 'Every X hours' ||
              (med as any)?.frequency === 'As needed') && (
              <div className="mt-4">
                <Controller
                  control={control}
                  name={`${fieldName}.frequencyDescription`}
                  render={({ field }) => {
                    const shouldShowError =
                      isFailValidation &&
                      (!field.value ||
                        (field.value && field.value.trim().length === 0));

                    return (
                      <>
                        <FloatingInput
                          id={`custom-frequency-${index}`}
                          label={
                            (med as any)?.frequency === 'As needed'
                              ? 'Describe when this medication is needed'
                              : 'Describe frequency (e.g., Every 6 hours)'
                          }
                          type="text"
                          placeholder="Enter custom frequency"
                          inputClass="mb-auto min-h-[52px]"
                          value={field.value || ''}
                          maxLength={128}
                          onChange={(e) => {
                            if (isFailValidation) {
                              setFailValidation(false);
                            }
                            field.onChange(e);
                          }}
                          onBlur={(e) => {
                            const trimmed = (e.target.value || '').trim();
                            if (trimmed !== e.target.value) {
                              field.onChange(trimmed);
                            }
                            field.onBlur();
                          }}
                        />
                        {shouldShowError && (
                          <FormMessageSpan
                            className="mt-1 text-sm"
                            text={'Custom frequency description is required.'}
                          />
                        )}
                      </>
                    );
                  }}
                />
              </div>
            )}
            <Controller
              control={control}
              name={`${fieldName}.description`}
              render={({ field }) => {
                const shouldShowError =
                  REQUIRE_DETAILS &&
                  isFailValidation &&
                  (!field.value ||
                    (field.value && field.value.trim().length === 0));

                return (
                  <>
                    <FloatingInput
                      id={`description-${index}`}
                      label="Can you tell us a bit about why this medication is being used?"
                      as="textarea"
                      rows={3}
                      inputClass="w-full mt-4 border rounded-[6px] p-2 text-sm min-h-60 max-xs:!pt-9"
                      value={field.value || ''}
                      maxLength={512}
                      onChange={(e) => {
                        if (isFailValidation) {
                          setFailValidation(false);
                        }
                        field.onChange(e);
                      }}
                      onBlur={field.onBlur}
                    />
                    {shouldShowError && (
                      <FormMessageSpan
                        className="mt-1 text-sm"
                        text={'Reason cannot be blank.'}
                      />
                    )}
                  </>
                );
              }}
            />
          </div>
        );
      })}
    </>
  );
};
